import {
  CALL_CONTEXT_SOURCES,
  CallContextSourceT,
} from "shared/lib/interfaces/email/call-context";

/**
 * A speaker turn in a transcript, e.g. "Rep: Hi Dana, caught you at an okay time?"
 */
export interface TranscriptTurnI {
  speaker: string;
  text: string;
}

/**
 * Matches a leading speaker label: a short name, then a colon.
 *
 * Deliberately conservative. A prose summary can contain a colon ("Two things:
 * pricing and timeline"), so the label must look like a name: 1-4 words, under
 * 32 characters, no sentence-ending punctuation.
 */
const SPEAKER_LINE = /^\s*([A-Z][\w.'-]*(?:\s+[\w.'-]+){0,3})\s*:\s*(.+)$/;

const MIN_TURNS_FOR_TRANSCRIPT = 3;

const isSpeakerLabel = (label: string) =>
  label.length <= 32 && !/[.!?]$/.test(label);

/**
 * Split pasted text into speaker turns.
 *
 * Consecutive lines without a speaker label are appended to the previous turn,
 * so a wrapped paragraph inside one person's answer stays in that turn.
 */
export const parseTranscriptTurns = (content: string): TranscriptTurnI[] => {
  const turns: TranscriptTurnI[] = [];

  content.split(/\r?\n/).forEach((line) => {
    if (!line.trim()) {
      return;
    }

    const match = line.match(SPEAKER_LINE);

    if (match && isSpeakerLabel(match[1])) {
      turns.push({ speaker: match[1].trim(), text: match[2].trim() });
      return;
    }

    if (turns.length > 0) {
      turns[turns.length - 1].text += ` ${line.trim()}`;
      return;
    }

    turns.push({ speaker: "", text: line.trim() });
  });

  return turns;
};

/**
 * Guess whether pasted text is a verbatim transcript or a written summary, so
 * the SDR does not have to classify their own paste before the tool will accept
 * it. Always overridable in the UI.
 */
export const detectCallContextSource = (
  content: string
): CallContextSourceT => {
  const labelled = parseTranscriptTurns(content).filter(
    (turn) => !!turn.speaker
  );

  // Two or more distinct speakers with enough back-and-forth reads as dialogue.
  const distinctSpeakers = new Set(
    labelled.map((turn) => turn.speaker.toLowerCase())
  );

  if (
    labelled.length >= MIN_TURNS_FOR_TRANSCRIPT &&
    distinctSpeakers.size >= 2
  ) {
    return CALL_CONTEXT_SOURCES.TRANSCRIPT;
  }

  return CALL_CONTEXT_SOURCES.SUMMARY;
};

/**
 * Rough spoken-duration estimate, shown so the SDR can sanity-check that they
 * pasted the whole call. ~150 words per minute of conversation.
 */
export const estimateCallMinutes = (content: string) => {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 150));
};
